import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "pa_token";
const USER_KEY = "pa_user";
const LAST_ROUTE_KEY = "pa_last_route";
const LAST_ROUTE_TS_KEY = "pa_last_route_ts";
// After this long with the app untouched, treat the next open as a fresh
// session and land on the Dashboard instead of wherever it was left open.
const LAST_ROUTE_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  save: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};
export const userStore = {
  get: () => {
    const s = localStorage.getItem(USER_KEY);
    return s ? JSON.parse(s) : null;
  },
  save: (u) => localStorage.setItem(USER_KEY, JSON.stringify(u)),
  clear: () => localStorage.removeItem(USER_KEY),
};
export const lastRouteStore = {
  get: () => {
    const path = localStorage.getItem(LAST_ROUTE_KEY);
    const savedAt = Number(localStorage.getItem(LAST_ROUTE_TS_KEY) || 0);
    if (!path || !savedAt || Date.now() - savedAt > LAST_ROUTE_MAX_AGE_MS) return null;
    return path;
  },
  save: (path) => {
    localStorage.setItem(LAST_ROUTE_KEY, path);
    localStorage.setItem(LAST_ROUTE_TS_KEY, String(Date.now()));
  },
  clear: () => {
    localStorage.removeItem(LAST_ROUTE_KEY);
    localStorage.removeItem(LAST_ROUTE_TS_KEY);
  },
};

const client = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

client.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const unwrap = (p) =>
  p.then((r) => r.data).catch((e) => {
    const msg = e?.response?.data?.detail || e?.message || "Request failed";
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  });

export const api = {
  login: (phone_number, password) =>
    unwrap(client.post("/auth/login", { phone_number, password })),
  me: () => unwrap(client.get("/auth/me")),

  // Students
  listStudents: () => unwrap(client.get("/students")),
  getStudent: (id) => unwrap(client.get(`/students/${id}`)),
  createStudent: (data) => unwrap(client.post("/students", data)),
  updateStudent: (id, data) => unwrap(client.put(`/students/${id}`, data)),
  deleteStudent: (id) => unwrap(client.delete(`/students/${id}`)),
  renewStudent: (id, new_end_date, amount) =>
    unwrap(client.post(`/students/${id}/renew`, { new_end_date, amount })),

  // Dashboard
  stats: (month) => unwrap(client.get("/stats", { params: month ? { month } : {} })),
  reminders: () => unwrap(client.get("/reminders")),
  feeReminders: () => unwrap(client.get("/fee-reminders")),

  // Coaches
  listCoaches: () => unwrap(client.get("/coaches")),
  createCoach: (data) => unwrap(client.post("/coaches", data)),
  updateCoach: (id, data) => unwrap(client.put(`/coaches/${id}`, data)),
  deleteCoach: (id) => unwrap(client.delete(`/coaches/${id}`)),

  // Attendance
  getStudentAttendance: (date) =>
    unwrap(client.get(`/attendance/students`, { params: { date } })),
  markStudentAttendance: (date, student_ids) =>
    unwrap(client.post("/attendance/students/mark", { date, student_ids })),
  markMyCoachAttendance: (date) =>
    unwrap(client.post("/attendance/coach/mark", { date })),
  myCoachAttendance: () => unwrap(client.get("/attendance/coach/me")),
  listCoachAttendance: (status_filter) =>
    unwrap(
      client.get("/attendance/coach", {
        params: status_filter ? { status_filter } : {},
      })
    ),
  reviewCoachAttendance: (id, status) =>
    unwrap(client.put(`/attendance/coach/${id}/review`, { status })),

  // Settings
  getSettings: () => unwrap(client.get("/settings")),
  updateSettings: (data) => unwrap(client.put("/settings", data)),

  // Activity
  activity: (limit = 30) =>
    unwrap(client.get("/activity", { params: { limit } })),

  // Student approvals (admin)
  listPendingStudents: () => unwrap(client.get("/students/pending")),
  approveStudent: (id) => unwrap(client.post(`/students/${id}/approve`)),
  rejectStudent: (id) => unwrap(client.post(`/students/${id}/reject`)),

  // Attendance date lists
  studentAttendanceDates: (id, month) =>
    unwrap(client.get(`/students/${id}/attendance-dates`, { params: month ? { month } : {} })),
  myCoachAttendanceDates: (month) =>
    unwrap(client.get("/attendance/coach/me/dates", { params: month ? { month } : {} })),
};